<template>
  <div>
    <v-row class="no-gutters align-center mb-4">
      <v-col class="col-auto mr-3">
        <h1 class="headline font-weight-bold black--text ma-0">
          <span v-if="isBillSuspended">{{
            $t("bills.billListSentbackTitle")
          }}</span>
          <span v-else>{{ $t("bills.billListTitle") }}</span>
        </h1>
      </v-col>
      <v-col v-if="showCreateBillButton" class="col-auto">
        <v-btn
          color="primary"
          class="caption font-weight-bold"
          outlined
          small
          rounded
          @click="createBill"
        >
          {{ $t("bills.bill.newBill.title") }}
        </v-btn>
      </v-col>
    </v-row>

    <slot name="filters"></slot>

    <v-data-table
      mobile-breakpoint="0"
      class="bills-table mt-3 elevation-1"
      :headers="tableHeaders"
      :items="items"
      :options.sync="paginationConfig"
      :server-items-length="totalItems"
      must-sort
      item-key="billId"
      :footer-props="{
        'items-per-page-options': itemsPerPage,
        'items-per-page-text': $t('table.itemsPerPageText')
      }"
    >
      <!-- NO DATA -->
      <template v-slot:no-data>
        <base-table-no-data-no-localized
          :filterCriteriaResults="filterCriteriaResults"
          :activeSortValue="''"
          :totalItems="totalItems"
        ></base-table-no-data-no-localized>
      </template>

      <!-- NO SEARCH RESULTS -->
      <template v-slot:no-results>
        <base-table-no-data-no-localized
          :filterCriteriaResults="filterCriteriaResults"
          :activeSortValue="''"
          :totalItems="totalItems"
        ></base-table-no-data-no-localized>
      </template>

      <template v-slot:item="{ item }">
        <bills-list-item
          :bill="item"
          :isBillSuspended="isBillSuspended"
        ></bills-list-item>
      </template>

      <template v-slot:[`footer.page-text`]="item">
        {{
          getVisibleDataMeta(item.pageStart, item.pageStop, item.itemsLength)
        }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import BillsListItem from "./BillsListItem";
import BaseTableNoDataNoLocalized from "../BaseTableNoDataNoLocalized";

export default {
  components: {
    BillsListItem,
    BaseTableNoDataNoLocalized
  },
  props: {
    items: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      itemsPerPage: [15, 30, 50, 100]
    };
  },
  methods: {
    getVisibleDataMeta(pageStart, pageStop, itemsLength) {
      const wordOF = this.$t("table.of");
      return `${pageStart} - ${pageStop} ${wordOF} ${itemsLength}`;
    },
    createBill() {
      this.$router.push({ name: "_newBill" });
    },
    retrieveItems() {
      this.$store.dispatch("bills/getItems", this.officeId);
    },
    isPaginationUpdated(paginationConfig) {
      return (
        this.order.sortBy[0] != paginationConfig.sortBy[0] ||
        this.order.descending[0] != paginationConfig.sortDesc[0] ||
        this.page != paginationConfig.page ||
        this.size != paginationConfig.itemsPerPage
      );
    }
  },
  computed: {
    hasWritePerm() {
      return this.$store.getters["auth/userHasRequiredAccess"](
        "RENDICONTAZIONE",
        "READWRITE"
      );
    },
    isBillSuspended() {
      return this.$store.getters["bills/getIsSuspended"];
    },
    showCreateBillButton() {
      return this.isBillSuspended ? false : this.hasWritePerm;
    },
    officeId() {
      return this.$store.getters["hfManagement/officeId"];
    },
    paginationConfig: {
      get() {
        return {
          page: this.page,
          itemsPerPage: this.size,
          sortBy: this.order.sortBy,
          sortDesc: this.order.descending
        };
      },
      set(paginationConfig) {
        if (this.isPaginationUpdated(paginationConfig)) {
          this.order = {
            descending: paginationConfig.sortDesc,
            sortBy: paginationConfig.sortBy
          };
          this.size = paginationConfig.itemsPerPage;
          this.page = paginationConfig.page;
          this.retrieveItems();
        }
      }
    },
    page: {
      get() {
        return this.$store.getters["bills/page"];
      },
      set(page) {
        this.$store.commit("bills/updatePage", page);
      }
    },
    size: {
      get() {
        return this.$store.getters["bills/size"];
      },
      set(size) {
        this.$store.commit("bills/updateSize", size);
      }
    },
    order: {
      get() {
        return this.$store.getters["bills/order"];
      },
      set(order) {
        this.$store.commit("bills/SET_ORDER", order);
      }
    },
    totalItems() {
      return this.$store.getters["bills/totalItems"];
    },
    filterCriteriaResults() {
      return this.$store.getters["bills/filterCriteriaResults"];
    },
    tableHeaders() {
      const headers = [];
      if (this.isBillSuspended) {
        headers.push({
          text: this.$t("bills.tableHeaders.sla"),
          value: "slaDueDate",
          sortable: true,
          width: "90px"
        });
      }
      headers.push(
        {
          text: this.$t("bills.tableHeaders.state"),
          value: "billState",
          sortable: false,
          width: "350px"
        },
        {
          text: this.$t("bills.tableHeaders.protocolCode"),
          value: "protocolCode",
          sortable: false
        }
      );
      if (this.isBillSuspended) {
        headers.push({
          text: this.$t("bills.tableHeaders.sentbackAt"),
          value: "sentBackAt",
          sortable: false,
          width: "200px"
        });
      }
      headers.push(
        {
          text: this.$t("bills.tableHeaders.lastUpdate"),
          value: "updatedAt",
          sortable: true,
          width: "250px"
        },
        {
          text: this.$t("bills.tableHeaders.numberOfServices"),
          value: "numberOfServices",
          sortable: true,
          width: "200px"
        },
        {
          value: "action",
          sortable: false,
          width: "120px"
        }
      );
      return headers;
    }
  }
};
</script>

<style scoped>
.bills-table {
  background-color: transparent;
}
</style>
